/**
 * Estado da Supervisão (spec 071) na URL — PURO de propósito: sem React, sem
 * alias `@/`, porque o web não tem executor de testes e a prova mora na API
 * (apps/api/src/shared/supervision-search.test.ts), como a do report-search.
 *
 * A aba, as mesas e o período ficam na URL: o supervisor deixa a página
 * aberta, manda o link para alguém ou volta de uma conversa e encontra a mesma
 * tela.
 */
import { z } from 'zod';
import { ATTENDANCE_PERIOD_MAX_DAYS } from '@erp/shared';

export const SUPERVISION_TABS = ['agora', 'periodo'] as const;
export type SupervisionTab = (typeof SUPERVISION_TABS)[number];

const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/** Lixo na URL vira o padrão, nunca uma tela quebrada (mesmo critério do report-search). */
export const supervisionSearchSchema = z.object({
  aba: z.enum(SUPERVISION_TABS).catch('agora').default('agora'),
  mesas: z.array(z.coerce.number().int().positive()).catch([]).optional(),
  inicio: isoDay.optional().catch(undefined),
  fim: isoDay.optional().catch(undefined),
});
export type SupervisionSearch = z.infer<typeof supervisionSearchSchema>;

/** o dia local de um instante, YYYY-MM-DD (o navegador está em São Paulo) */
export function localDay(at: Date): string {
  const y = at.getFullYear();
  const m = String(at.getMonth() + 1).padStart(2, '0');
  const d = String(at.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function addDays(day: string, n: number): string {
  const d = new Date(`${day}T12:00:00`);
  d.setDate(d.getDate() + n);
  return localDay(d);
}

export type PeriodPreset = 'hoje' | '7d' | '30d' | 'mes';

export const PERIOD_PRESET_LABELS: Record<PeriodPreset, string> = {
  hoje: 'Hoje',
  '7d': '7 dias',
  '30d': '30 dias',
  mes: 'Este mês',
};

export function presetPeriod(preset: PeriodPreset, today: string): { start: string; end: string } {
  switch (preset) {
    case 'hoje':
      return { start: today, end: today };
    case '7d':
      return { start: addDays(today, -6), end: today };
    case '30d':
      return { start: addDays(today, -29), end: today };
    case 'mes':
      return { start: `${today.slice(0, 8)}01`, end: today };
  }
}

/**
 * O período que vale: o da URL quando é válido; senão os últimos 7 dias. Fim
 * antes do início troca os dois; acima do teto da API, o início anda até
 * caber — a tela nunca pede o que a API recusaria.
 */
export function effectivePeriod(
  search: SupervisionSearch,
  today: string
): { start: string; end: string } {
  if (!search.inicio || !search.fim) return presetPeriod('7d', today);
  let start = search.inicio;
  let end = search.fim;
  if (end < start) [start, end] = [end, start];
  const earliest = addDays(end, -(ATTENDANCE_PERIOD_MAX_DAYS - 1));
  if (start < earliest) start = earliest;
  return { start, end };
}

/** qual atalho o período corresponde, para marcar o botão (null = personalizado) */
export function matchingPreset(
  period: { start: string; end: string },
  today: string
): PeriodPreset | null {
  for (const p of Object.keys(PERIOD_PRESET_LABELS) as PeriodPreset[]) {
    const x = presetPeriod(p, today);
    if (x.start === period.start && x.end === period.end) return p;
  }
  return null;
}
